/**
 * Knowledge Base Page - Phase 10
 * 
 * Browse and search reusable security/review knowledge from the rule repository.
 * Read-only interface focused on reviewer needs (no admin/governance features).
 */

import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { api, KBEntryListItem, KBStatsResponse, KBPackListItem } from '../lib/api'
import { LoadingSpinner } from '../components/design-system/Loading'
import { EmptyState } from '../components/design-system/EmptyState'
import { Badge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { useLocale } from '../contexts/LocaleContext'
import { formatLabel, packDisplayName, severityLabel } from '../lib/i18n'

export function KnowledgeBasePage() {
  const { t, locale } = useLocale()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [entries, setEntries] = useState<KBEntryListItem[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [stats, setStats] = useState<KBStatsResponse | null>(null)
  const [packs, setPacks] = useState<KBPackListItem[]>([])
  
  // Filters from URL
  const search = searchParams.get('search') || ''
  const category = searchParams.get('category') || ''
  const severity = searchParams.get('severity') || ''
  const formatType = searchParams.get('format_type') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)
  const pageSize = 20
  
  useEffect(() => {
    loadKBData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, category, severity, formatType, page])
  
  async function loadKBData() {
    try {
      setLoading(true)
      setError(null)
      
      const [listData, statsData, packData] = await Promise.all([
        api.listKBEntries({
          search: search || undefined,
          category: category || undefined,
          severity: severity || undefined,
          format_type: formatType || undefined,
          page,
          page_size: pageSize,
        }),
        api.getKBStats(),
        api.listKBPacks({ page: 1, page_size: 20 }),
      ])
      
      setEntries(listData.items)
      setTotalCount(listData.total_count)
      setTotalPages(listData.total_pages)
      setStats(statsData)
      setPacks(packData.items)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('kb.failed'))
    } finally {
      setLoading(false)
    }
  }
  
  function updateFilter(key: string, value: string) {
    const newParams = new URLSearchParams(searchParams)
    if (value) {
      newParams.set(key, value)
    } else {
      newParams.delete(key)
    }
    newParams.delete('page') // Reset to page 1 when filtering
    setSearchParams(newParams)
  }
  
  function clearFilters() {
    setSearchParams({})
  }
  
  function goToPage(newPage: number) {
    const newParams = new URLSearchParams(searchParams)
    newParams.set('page', newPage.toString())
    setSearchParams(newParams)
  }
  
  const hasFilters = search || category || severity || formatType
  
  // Loading state
  if (loading && entries.length === 0) {
    return (
      <div style={{ padding: '24px' }} data-testid="knowledge-base-page">
        <h1
          style={{
            margin: 0,
            marginBottom: '16px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-2xl)',
            fontWeight: 600,
            color: 'var(--reviewer-text)',
          }}
        >
          {t('nav.knowledgeBase')}
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '320px' }}>
          <LoadingSpinner size="lg" text={t('kb.loading')} />
        </div>
      </div>
    )
  }
  
  // Error state
  if (error) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px' }}>
        <div
          style={{
            border: '1px solid var(--reviewer-danger-border)',
            backgroundColor: 'var(--reviewer-danger-soft)',
            padding: '24px',
          }}
        >
          <h2 style={{ 
            margin: 0, 
            marginBottom: '12px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-lg)',
            color: 'var(--reviewer-danger)',
          }}>
            {t('kb.failed')}
          </h2>
          <p style={{ 
            margin: 0, 
            marginBottom: '16px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text)',
          }}>
            {error}
          </p>
          <Button onClick={loadKBData} variant="danger">
            {t('common.retry')}
          </Button>
        </div>
      </div>
    )
  }
  
  return (
    <div style={{ padding: '24px' }} data-testid="knowledge-base-page">
      {/* Header */}
      <div style={{ marginBottom: '32px' }}>
        <h1
          style={{
            margin: 0,
            marginBottom: '8px',
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-2xl)',
            fontWeight: 600,
            color: 'var(--reviewer-text)',
          }}
        >
          {t('nav.knowledgeBase')}
        </h1>
        <p
          style={{
            margin: 0,
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text-muted)',
          }}
        >
          {t('kb.lead')}
        </p>
      </div>

      {packs.length > 0 && (
        <div data-testid="knowledge-base-packs" style={{ marginBottom: '24px' }}>
          <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: '16px' }}>{t('kb.activePacks')}</h2>
          <ul style={{ fontFamily: 'var(--reviewer-font-mono)', paddingLeft: '18px' }}>
            {packs.map((pack) => (
              <li key={pack.id}>
                {t('kb.packLine', {
                  name: packDisplayName(locale, pack.pack_id, pack.name),
                  id: pack.pack_id,
                  version: pack.version,
                  n: pack.member_count,
                })}
              </li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Stats Summary */}
      {stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '24px',
          }}
        >
          <div
            style={{
              padding: '16px',
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-2xl)',
                fontWeight: 600,
                color: 'var(--reviewer-primary)',
                marginBottom: '4px',
              }}
            >
              {stats.total_entries}
            </div>
            <div
              style={{
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-sm)',
                color: 'var(--reviewer-text-muted)',
              }}
            >
              {t('kb.totalEntries')}
            </div>
          </div>
          
          <div
            style={{
              padding: '16px',
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-2xl)',
                fontWeight: 600,
                color: 'var(--reviewer-text)',
                marginBottom: '4px',
              }}
            >
              {Object.keys(stats.by_category).length}
            </div>
            <div
              style={{
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-sm)',
                color: 'var(--reviewer-text-muted)',
              }}
            >
              {t('kb.categories')}
            </div>
          </div>
          
          <div
            style={{
              padding: '16px',
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
            }}
          >
            <div
              style={{
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-2xl)',
                fontWeight: 600,
                color: 'var(--reviewer-text)',
                marginBottom: '4px',
              }}
            >
              {Object.keys(stats.by_format).length}
            </div>
            <div
              style={{
                fontFamily: 'var(--reviewer-font-mono)',
                fontSize: 'var(--reviewer-text-sm)',
                color: 'var(--reviewer-text-muted)',
              }}
            >
              {t('kb.formats')}
            </div>
          </div>
        </div>
      )}
      
      {/* Filters */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <input
            type="text"
            placeholder={t('kb.search')}
            value={search}
            onChange={(e) => updateFilter('search', e.target.value)}
            style={{
              flex: '1 1 300px',
              padding: '8px 12px',
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
              color: 'var(--reviewer-text)',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
            }}
          />
          
          <select
            value={severity}
            onChange={(e) => updateFilter('severity', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
              color: 'var(--reviewer-text)',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
            }}
          >
            <option value="">{t('kb.allSeverities')}</option>
            <option value="critical">{t('severity.critical')}</option>
            <option value="high">{t('severity.high')}</option>
            <option value="medium">{t('severity.medium')}</option>
            <option value="low">{t('severity.low')}</option>
            <option value="info">{t('severity.info')}</option>
          </select>
          
          <select
            value={category}
            onChange={(e) => updateFilter('category', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
              color: 'var(--reviewer-text)',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
            }}
          >
            <option value="">{t('kb.allCategories')}</option>
            <option value="vulnerability">{t('kb.cat.vulnerability')}</option>
            <option value="code_smell">{t('kb.cat.code_smell')}</option>
            <option value="maintainability">{t('kb.cat.maintainability')}</option>
            <option value="security">{t('kb.cat.security')}</option>
            <option value="performance_issue">{t('kb.cat.performance_issue')}</option>
            <option value="accessibility">{t('kb.cat.accessibility')}</option>
          </select>
          
          <select
            value={formatType}
            onChange={(e) => updateFilter('format_type', e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1px solid var(--reviewer-border)',
              backgroundColor: 'var(--reviewer-surface)',
              color: 'var(--reviewer-text)',
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-sm)',
            }}
          >
            <option value="">{t('kb.allFormats')}</option>
            <option value="dockerfile">{t('fmt.dockerfile')}</option>
            <option value="kubernetes">{t('fmt.kubernetes')}</option>
            <option value="terraform">{t('fmt.terraform')}</option>
            <option value="ansible">{t('fmt.ansible')}</option>
          </select>
          
          {hasFilters && (
            <Button onClick={() => navigate("/knowledge-base")} variant="secondary">
              {t('kb.clearFilters')}
            </Button>
          )}
        </div>
        
        <div
          style={{
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text-muted)',
          }}
        >
          {t('kb.showing', { shown: entries.length, total: totalCount })}
        </div>
      </div>
      
      {/* Results */}
      {entries.length === 0 ? (
        <EmptyState
          title={hasFilters ? t('kb.emptyFilter') : t('kb.emptyTitle')}
          description={hasFilters ? t('packs.emptyFilter') : t('kb.empty')}
        >
          {hasFilters && (
            <Button onClick={clearFilters} variant="primary">
              {t('kb.clearFilters')}
            </Button>
          )}
        </EmptyState>
      ) : (
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '24px' }}>
            {entries.map((entry) => (
              <div
                key={entry.id}
                onClick={() => navigate(`/knowledge-base/${entry.id}`)}
                style={{
                  padding: '16px',
                  border: '1px solid var(--reviewer-border)',
                  backgroundColor: 'var(--reviewer-surface)',
                  cursor: 'pointer',
                  transition: 'border-color 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--reviewer-primary)'
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--reviewer-border)'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                  <h3
                    style={{
                      margin: 0,
                      fontFamily: 'var(--reviewer-font-mono)',
                      fontSize: 'var(--reviewer-text-base)',
                      fontWeight: 600,
                      color: 'var(--reviewer-text)',
                    }}
                  >
                    {entry.title}
                  </h3>
                  <Badge variant={entry.severity === 'critical' || entry.severity === 'high' ? 'danger' : 'warning'}>
                    {locale === 'en' ? entry.severity.toUpperCase() : severityLabel(locale, entry.severity)}
                  </Badge>
                </div>
                
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                  <Badge variant="default">{t(`kb.cat.${entry.category}`) || entry.category.replace('_', ' ')}</Badge>
                  <Badge variant="default">{formatLabel(locale, entry.format_type)}</Badge>
                  <Badge variant="default">v{entry.version}</Badge>
                  {entry.has_remediation && <Badge variant="success">{t('kb.hasFix')}</Badge>}
                </div>
                
                <div
                  style={{
                    fontFamily: 'var(--reviewer-font-mono)',
                    fontSize: 'var(--reviewer-text-sm)',
                    color: 'var(--reviewer-text-muted)',
                  }}
                >
                  {t('kb.ruleId', { id: entry.rule_id })}
                </div>
              </div>
            ))}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', alignItems: 'center' }}>
              <Button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                variant="secondary"
              >
                {t('common.previous')}
              </Button>
              <span
                style={{
                  fontFamily: 'var(--reviewer-font-mono)',
                  fontSize: 'var(--reviewer-text-sm)',
                  color: 'var(--reviewer-text)',
                }}
              >
                {t('common.pageOf', { page, total: totalPages })}
              </span>
              <Button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                variant="secondary"
              >
                {t('common.next')}
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
